import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { Task } from "../../types";

/**
 * Returns a `moveSubtask(subtaskId, sourceTaskId, targetTaskId)` that
 * optimistically re-parents a single subtask from one task to another in the
 * tasks cache — reflected instantly in every consumer of `useTasks` (e.g. the
 * open task dialog) — then commits the DB write and rolls back on failure.
 * Mirrors {@link useUpdateSubtask}, scoped to a subtask move between parent
 * tasks.
 *
 * The moved subtask is appended to the end of the target task's subtask list
 * (`order_position` = the highest existing position + 100), matching
 * the pre-migration moveSubtask. Like that original, **no Google Calendar sync fires
 * on a move** — only the DB row is re-pointed (stored attachment files are left
 * under the original parent's folder, the same way the god-hook left them). On
 * failure it restores the pre-mutation snapshot, captures the error to Sentry,
 * surfaces a toast, and returns `false`.
 *
 * This is the query-cache-aware replacement for the pre-migration move.
 */
export function useMoveSubtask() {
  const queryClient = useQueryClient();

  return async (
    subtaskId: string,
    sourceTaskId: string,
    targetTaskId: string,
  ): Promise<boolean> => {
    // Snapshot before mutating so a failed write can be rolled back.
    const previousTasks = queryClient.getQueryData<Task[]>(queryKeys.tasks);

    const targetTask = (previousTasks ?? []).find((t) => t.id === targetTaskId);
    const maxPos = Math.max(
      ...(targetTask?.subtasks?.map((st) => st.order_position) || [0]),
      0,
    );
    const newOrderPosition = maxPos + 100;

    const sourceTask = (previousTasks ?? []).find((t) => t.id === sourceTaskId);
    const movedSubtask = sourceTask?.subtasks?.find((st) => st.id === subtaskId);

    // Optimistic move — reflected instantly in every consumer of useTasks.
    // Left alone when the cache has not loaded: writing `[]` into it would flip
    // useTasks out of its pending state and drop the app's loading gate, and
    // the rollback's snapshot guard could not put it back.
    queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
      prev
        ? prev.map((t) => {
            if (t.id === sourceTaskId) {
              return {
                ...t,
                subtasks: t.subtasks?.filter((st) => st.id !== subtaskId),
              };
            }
            if (t.id === targetTaskId && movedSubtask) {
              return {
                ...t,
                subtasks: [
                  ...(t.subtasks || []),
                  {
                    ...movedSubtask,
                    task_id: targetTaskId,
                    order_position: newOrderPosition,
                  },
                ],
              };
            }
            return t;
          })
        : prev,
    );

    const { error } = await supabase
      .from("subtasks")
      .update({
        task_id: targetTaskId,
        order_position: newOrderPosition,
        updated_at: new Date().toISOString(),
      })
      .eq("id", subtaskId);

    if (error) {
      // Restore the whole snapshot if the move fails.
      if (previousTasks) {
        queryClient.setQueryData(queryKeys.tasks, previousTasks);
      }
      Sentry.captureException(error, {
        extra: {
          mutation: "useMoveSubtask",
          subtaskId,
          sourceTaskId,
          targetTaskId,
        },
      });
      toast.error("Failed to move subtask. Please try again.");
      return false;
    }

    queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    return true;
  };
}
